import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { analyticsEventSchema } from "@/lib/calculator-schemas";

export const logAnalyticsEvent = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => analyticsEventSchema.parse(input))
  .handler(async ({ data }) => {
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const { recordEvent } = await import("@/lib/analytics-events.server");
    try {
      await recordEvent(
        {
          event_name: data.event_name,
          session_id: data.session_id || undefined,
          path: data.path || undefined,
          referrer: data.referrer || undefined,
          props: data.props,
        },
        getRequestHeader("user-agent") ?? null,
      );
    } catch (e) {
      console.error("[logAnalyticsEvent] failed", e);
      return { ok: false };
    }
    return { ok: true };
  });

export const adminAnalyticsSummary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ days: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30) }).parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("@/lib/calculator-admin.server");
    await assertAdmin(context);
    const { summarize } = await import("@/lib/analytics-events.server");
    return summarize(data.days);
  });

export const adminConversionsReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        days: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30),
        campaign: z.string().max(200).optional().nullable(),
        type: z.enum(["all", "call_click", "text_click", "form_submit"]).default("all"),
        source: z.string().max(100).optional().nullable(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("@/lib/calculator-admin.server");
    await assertAdmin(context);
    const { conversionsReport } = await import("@/lib/analytics-events.server");
    return conversionsReport(data);
  });
